import { useState } from 'react';
import styled from 'styled-components';
import {
  blobErrorText,
  useLazyDownloadPayRequestFileQuery,
  type PayRequestFile,
} from '@shared/api';
import { formatBytes } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import {
  IconFile,
  IconFileArchive,
  IconFileImage,
  IconFileSpreadsheet,
  IconFileText,
} from '@shared/ui/icons';
import { fileExtension, formatDateTime, opensInBrowser } from '../model';
import { ErrorText, Small } from './styled';

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const Icon = styled.span`
  display: inline-flex;
  flex: none;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Texts = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

function FileIcon({ name }: { name: string }) {
  const ext = fileExtension(name);
  if (['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tif', 'tiff', 'heic', 'heif'].includes(ext)) {
    return <IconFileImage size={18} />;
  }
  if (['xlsx', 'xls', 'ods'].includes(ext)) return <IconFileSpreadsheet size={18} />;
  if (ext === 'zip') return <IconFileArchive size={18} />;
  if (['pdf', 'doc', 'docx', 'odt', 'rtf', 'txt', 'xml'].includes(ext)) {
    return <IconFileText size={18} />;
  }
  return <IconFile size={18} />;
}

function saveUrl(url: string, name: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

interface Props {
  requestId: string;
  files: PayRequestFile[];
}

/**
 * Файлы заявки. Скачивание — с токеном (`fetch` → Blob → ссылка): PDF и фото
 * открываются в новой вкладке, остальное сохраняется на компьютер.
 */
export function FilesList({ requestId, files }: Props) {
  const [download] = useLazyDownloadPayRequestFileQuery();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(file: PayRequestFile) {
    setError(null);
    const inline = opensInBrowser(file.file_name);
    // Вкладку открываем сразу по нажатию — иначе браузер её заблокирует;
    // адрес подставим, когда файл придёт.
    const tab = inline ? window.open('', '_blank') : null;
    if (tab) {
      tab.document.title = file.file_name;
      if (tab.document.body) tab.document.body.textContent = 'Открываем файл…';
    }
    setBusyId(file.id);
    try {
      const blob = await download({ id: requestId, fileId: file.id }).unwrap();
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else saveUrl(url, blob instanceof File ? blob.name : file.file_name);
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      setError(await blobErrorText(err, `Файл «${file.file_name}» не открылся.`));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <List>
        {files.map((f) => {
          const inline = opensInBrowser(f.file_name);
          return (
            <Item key={f.id}>
              <Icon aria-hidden="true">
                <FileIcon name={f.file_name} />
              </Icon>
              <Texts>
                <Name>{f.file_name}</Name>
                <Small>
                  {formatBytes(f.size_bytes)}
                  {f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''} ·{' '}
                  {formatDateTime(f.uploaded_at)}
                </Small>
              </Texts>
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                onClick={() => void open(f)}
                disabled={busyId === f.id}
                aria-label={`${inline ? 'Открыть' : 'Скачать'} ${f.file_name}`}
              >
                {busyId === f.id ? 'Открываем…' : inline ? 'Открыть' : 'Скачать'}
              </Button>
            </Item>
          );
        })}
      </List>
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </>
  );
}
